// Lock session (I-1a Batch 4): the domain layer between the S31 / S32 screens and
// crypto/ + store/ (screen → domain → store; screens never touch storage).
//
// - status(): is there a keyring (else first run), is there a recovery code, and
//   how long until the next try is allowed.
// - setUp(): first run. Make the data key, wrap it under the app-lock secret (and
//   the optional recovery code) at the Argon2id floor, and save the wraps with
//   createKeyring (add-only, one transaction). False from createKeyring means
//   another copy finished first run already → the caller goes to Unlock.
// - unlock(): honour the wrong-try delay BEFORE any Argon2id work; a wrong secret
//   counts one failed try (a counter without times, IR-22); success returns the
//   count since the last unlock (shown once) and resets it. There is NO auto-wipe.
// - replaceAppLock(): after opening with the recovery code, wrap the same data key
//   under a new app-lock secret (no record is re-encrypted).
// - deleteEverything(): "I can't open it" → crypto-shredding without the key.
// - Backup & move (I-1a Batch 6b; Implementation Plan v02 §6.4): makeBackupFile()
//   seals what export/collectBackupContents carries into a `.feelbackup`;
//   noteBackupSaved() notes the day in the install record once the file was
//   handed to the browser; openBackupFile() checks and opens a
//   file (writes nothing); restoreBackup() is first run from a backup — a new
//   app lock, a NEW data key, every record re-sealed under it — into an EMPTY
//   diary only (IP2-36 (c)). After a backup or a restore the browser is asked to
//   keep this app's storage (`navigator.storage.persist()`).
//
// The wrong-try wait is counted from the last failed try in this page, or from
// page load after a reload (the count is stored, times are not).
//
// Argon2id runs on the main thread (IP2-29 (4)); the screen draws "Opening…" and
// yields a frame before calling in here. Its WebAssembly is loaded lazily and
// once; preload() starts it early so the first try is quicker and the files are
// in the service-worker cache for offline unlock.

import type { computeHash } from 'argon2id'

import {
  type Bytes,
  recoveryCodeBytes,
  rewrapDataKey,
  secretBytes,
  setUpDataKey,
  unlockDelaySeconds,
  unwrapDataKey,
  type WrapPurpose,
  WrongSecretError,
} from '../crypto'
import {
  BACKUP_FILE_EXTENSION,
  type BackupRefusal,
  BackupRefusedError,
  type BackupReminder,
  backupReminder,
  type BackupVersions,
  collectBackupContents,
  makeBackup,
  openBackup,
  type OpenedBackup,
} from '../export'
import { HELP_PACKAGE } from '../safety/helpPackage.generated'
import {
  browserSpaceEstimate,
  createKeyring,
  DB_NAME,
  destroyStore,
  EncryptedStore,
  hasKeyring,
  isStorageFullError,
  type LockManagerLike,
  openStoreDb,
  readInstallRecord,
  readKeyWrap,
  recordBackupMade,
  recordFailedUnlock,
  replaceKeyWrap,
  resetFailedUnlocks,
  restoreIntoEmpty,
  type SpaceEstimate,
  type StoreDb,
} from '../store'

import { checkSecret } from './secretPolicy'

/** The two calls of StorageManager used here (injectable for tests). */
export type PersistentStorage = Pick<StorageManager, 'persist' | 'persisted'>

export interface LockSessionOptions {
  readonly dbName?: string
  readonly locks?: LockManagerLike
  /** Defaults to the browser loader (WebAssembly fetched from our own address). */
  readonly loadHash?: () => Promise<computeHash>
  readonly now?: () => number
  /** Defaults to `navigator.storage`; null = never ask. */
  readonly storage?: PersistentStorage | null
  /** Judges an unclear failed write (restore, and the stores opened here). Defaults to `navigator.storage`; null = never ask. */
  readonly space?: SpaceEstimate | null
}

export interface LockStatus {
  readonly hasKeyring: boolean
  readonly hasRecoveryCode: boolean
  /** Whole seconds until the next try is allowed; 0 = now. */
  readonly waitSeconds: number
}

export type FirstRunResult = { readonly kind: 'opened'; readonly store: EncryptedStore } | { readonly kind: 'already-set-up' }

export type UnlockResult =
  | { readonly kind: 'opened'; readonly store: EncryptedStore; readonly failedTries: number }
  /** Wrong secret; counted. */
  | { readonly kind: 'wrong'; readonly waitSeconds: number }
  /** Still inside the wrong-try delay; nothing was tried. */
  | { readonly kind: 'wait'; readonly waitSeconds: number }
  /** Empty, or (recovery) cannot be a recovery code; nothing was tried or counted. */
  | { readonly kind: 'not-a-code' }
  /** No usable wrap for this way of opening (missing or damaged). */
  | { readonly kind: 'cannot-open' }

/** A made backup file: its bytes and a neutral file name (date only, no content). */
export interface BackupFile {
  readonly bytes: Bytes
  readonly fileName: string
}

export type OpenBackupResult =
  | { readonly kind: 'opened'; readonly backup: OpenedBackup }
  /** Checked and refused; nothing was written. */
  | { readonly kind: 'refused'; readonly reason: BackupRefusal }

export type RestoreResult =
  | { readonly kind: 'opened'; readonly store: EncryptedStore }
  /** Another copy finished first run meanwhile: its diary stays; go to Unlock. */
  | { readonly kind: 'already-set-up' }
  /** No lock, but records or photos are left on this phone: a restore needs an empty diary. */
  | { readonly kind: 'not-empty' }
  /** The browser refused the write for space; the one transaction wrote nothing. */
  | { readonly kind: 'storage-full' }

export class RefusedSecretError extends Error {
  constructor() {
    super('the new app-lock secret does not meet the lock rules')
    this.name = 'RefusedSecretError'
  }
}

function browserHash(): Promise<computeHash> {
  return import('../crypto/argon2Browser').then(async (m) => (await m.loadArgon2InBrowser()).hash)
}

function browserStorage(): PersistentStorage | null {
  return typeof navigator !== 'undefined' && navigator.storage ? navigator.storage : null
}

/** About how many bytes a restore writes: the photo blobs plus the records' data. */
function restoreSize(backup: OpenedBackup): number {
  const blobs = backup.contents.blobs.reduce((sum, blob) => sum + blob.ct.byteLength, 0)
  return blobs + JSON.stringify(backup.contents.records).length
}

/** The local day, YYYY-MM-DD (no time is kept). */
function localDay(ms: number): string {
  const date = new Date(ms)
  const two = (n: number): string => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`
}

/** What a new backup file's header says about the versions that made it. */
function backupVersions(store: EncryptedStore): BackupVersions {
  return {
    app: __APP_BUILD__.commit.trim().slice(0, 40) || 'dev',
    schema: store.recordsVersion,
    packages: { [HELP_PACKAGE.id]: HELP_PACKAGE.version },
  }
}

export class LockSession {
  private readonly dbName: string
  private readonly locks: LockManagerLike | undefined
  private readonly loadHash: () => Promise<computeHash>
  private readonly now: () => number
  private readonly storage: PersistentStorage | null
  private readonly space: SpaceEstimate | null
  private hashPromise: Promise<computeHash> | null = null
  /** When the current wrong-try wait started: the last failed try, or page load. */
  private waitFrom: number

  constructor(options: LockSessionOptions = {}) {
    this.dbName = options.dbName ?? DB_NAME
    this.locks = options.locks
    this.loadHash = options.loadHash ?? browserHash
    this.now = options.now ?? (() => Date.now())
    this.storage = options.storage === undefined ? browserStorage() : options.storage
    this.space = options.space === undefined ? browserSpaceEstimate() : options.space
    this.waitFrom = this.now()
  }

  /** Start loading Argon2id now (no error surfaces here; a later try retries). */
  preload(): void {
    this.hash().catch(() => undefined)
  }

  async status(): Promise<LockStatus> {
    return this.withDb(async (db) => ({
      hasKeyring: await hasKeyring(db),
      hasRecoveryCode: (await readKeyWrap(db, 'recovery')) !== null,
      waitSeconds: this.remaining((await readInstallRecord(db))?.failedUnlocks ?? 0),
    }))
  }

  async setUp(secret: string, recoveryCode: string | null): Promise<FirstRunResult> {
    if (!checkSecret(secret).ok) {
      throw new RefusedSecretError()
    }
    const recovery = recoveryCode === null ? undefined : recoveryCodeBytes(recoveryCode)
    if (recovery === null) {
      throw new Error('not a recovery code')
    }
    const hash = await this.hash()
    const keys = await setUpDataKey(hash, secretBytes(secret), recovery)
    const created = await this.withDb((db) => createKeyring(db, keys.wraps))
    if (!created) {
      return { kind: 'already-set-up' }
    }
    return { kind: 'opened', store: await this.openStore(keys.dataKey) }
  }

  async unlock(input: string, via: WrapPurpose): Promise<UnlockResult> {
    const before = await this.withDb(async (db) => ({
      failed: (await readInstallRecord(db))?.failedUnlocks ?? 0,
      wrap: await readKeyWrap(db, via),
    }))
    const wait = this.remaining(before.failed)
    if (wait > 0) {
      return { kind: 'wait', waitSeconds: wait }
    }
    const secret = via === 'recovery' ? recoveryCodeBytes(input) : input.length > 0 ? secretBytes(input) : null
    if (secret === null) {
      return { kind: 'not-a-code' }
    }
    if (before.wrap === null) {
      return { kind: 'cannot-open' }
    }

    const hash = await this.hash()
    let dataKey: CryptoKey
    try {
      dataKey = await unwrapDataKey(hash, before.wrap, secret)
    } catch (error) {
      if (!(error instanceof WrongSecretError)) {
        // Damaged wrap or a cost outside floor..ceiling: not the person's mistake.
        return { kind: 'cannot-open' }
      }
      const failed = await this.withDb((db) => recordFailedUnlock(db))
      this.waitFrom = this.now()
      return { kind: 'wrong', waitSeconds: unlockDelaySeconds(failed) }
    }

    const store = await this.openStore(dataKey)
    const failedTries = await this.withDb(async (db) => {
      const count = (await readInstallRecord(db))?.failedUnlocks ?? 0
      await resetFailedUnlocks(db)
      return count
    })
    return { kind: 'opened', store, failedTries }
  }

  /** After a recovery-code unlock: the same data key, wrapped under a new app-lock secret. */
  async replaceAppLock(recoveryCode: string, newSecret: string): Promise<void> {
    if (!checkSecret(newSecret).ok) {
      throw new RefusedSecretError()
    }
    const recovery = recoveryCodeBytes(recoveryCode)
    const wrap = await this.withDb((db) => readKeyWrap(db, 'recovery'))
    if (recovery === null || wrap === null) {
      throw new Error('no recovery wrap to re-wrap from')
    }
    const hash = await this.hash()
    const next = await rewrapDataKey(hash, wrap, recovery, secretBytes(newSecret), 'app-lock')
    await this.withDb((db) => replaceKeyWrap(db, next))
  }

  // --- Backup & move (I-1a Batch 6b) ------------------------------------------

  /** The S33 reminder line: no backup made on this phone, or the last one is old. */
  async backupReminder(): Promise<BackupReminder> {
    const last = await this.withDb(async (db) => (await readInstallRecord(db))?.lastBackup ?? null)
    return backupReminder(last, localDay(this.now()))
  }

  /**
   * Seal the open diary into one `.feelbackup` file under the backup
   * passphrase (refused before any key work if it fails the backup policy).
   * Reading only: also allowed in a read-only copy. Writes nothing.
   */
  async makeBackupFile(store: EncryptedStore, passphrase: string): Promise<BackupFile> {
    const contents = await collectBackupContents(store)
    const bytes = await makeBackup(await this.hash(), passphrase, backupVersions(store), contents)
    return { bytes, fileName: `backup-${localDay(this.now())}${BACKUP_FILE_EXTENSION}` }
  }

  /** The file was handed to the browser: note the day (install record) and ask to keep storage. */
  async noteBackupSaved(): Promise<void> {
    await this.withDb((db) => recordBackupMade(db, localDay(this.now())))
    this.keepStorage()
  }

  /** Check and open a backup file with its passphrase. Writes nothing. */
  async openBackupFile(file: Uint8Array, passphrase: string): Promise<OpenBackupResult> {
    try {
      return { kind: 'opened', backup: await openBackup(await this.hash(), file, passphrase) }
    } catch (error) {
      if (error instanceof BackupRefusedError) {
        return { kind: 'refused', reason: error.reason }
      }
      throw error
    }
  }

  /**
   * First run from a backup: make a NEW data key, wrap it under the new app-lock
   * secret (and the optional recovery code), re-seal every record under it and
   * write it all into the empty diary in one transaction. Takes only a backup
   * that openBackupFile has already checked and opened.
   */
  async restoreBackup(backup: OpenedBackup, secret: string, recoveryCode: string | null): Promise<RestoreResult> {
    if (!checkSecret(secret).ok) {
      throw new RefusedSecretError()
    }
    const recovery = recoveryCode === null ? undefined : recoveryCodeBytes(recoveryCode)
    if (recovery === null) {
      throw new Error('not a recovery code')
    }
    const hash = await this.hash()
    const keys = await setUpDataKey(hash, secretBytes(secret), recovery)
    let outcome
    try {
      outcome = await this.withDb((db) =>
        restoreIntoEmpty(db, keys.dataKey, {
          wraps: keys.wraps,
          records: backup.contents.records,
          blobs: backup.contents.blobs,
          schemaVersion: backup.versions.schema,
        }),
      )
    } catch (error) {
      if (await isStorageFullError(error, restoreSize(backup), this.space)) {
        return { kind: 'storage-full' }
      }
      throw error
    }
    if (outcome !== 'written') {
      return { kind: outcome }
    }
    const store = await this.openStore(keys.dataKey)
    this.keepStorage()
    return { kind: 'opened', store }
  }

  /**
   * Ask the browser to keep this app's storage (no prompt on Android Chrome for
   * an installed app; device check A8). Never blocks and never fails the caller.
   */
  private keepStorage(): void {
    const storage = this.storage
    if (!storage) {
      return
    }
    void (async () => {
      try {
        if (!(await storage.persisted())) {
          await storage.persist()
        }
      } catch {
        // Not granted or not available: the backup file is the real safety net.
      }
    })()
  }

  /** Delete everything without the key (crypto-shredding first). The caller must have closed any open store. */
  async deleteEverything(onBlocked?: () => void): Promise<void> {
    await destroyStore(this.dbName, onBlocked)
    this.waitFrom = this.now()
  }

  private remaining(failedTries: number): number {
    const elapsed = (this.now() - this.waitFrom) / 1000
    return Math.max(0, Math.ceil(unlockDelaySeconds(failedTries) - elapsed))
  }

  private hash(): Promise<computeHash> {
    if (!this.hashPromise) {
      this.hashPromise = this.loadHash().catch((error: unknown) => {
        this.hashPromise = null
        throw error
      })
    }
    return this.hashPromise
  }

  private openStore(dataKey: CryptoKey): Promise<EncryptedStore> {
    const options = { dataKey, dbName: this.dbName, space: this.space }
    return EncryptedStore.open(this.locks ? { ...options, locks: this.locks } : options)
  }

  private async withDb<T>(run: (db: StoreDb) => Promise<T>): Promise<T> {
    const db = await openStoreDb(this.dbName)
    try {
      return await run(db)
    } finally {
      db.close()
    }
  }
}
